// Load Board V1 — Web (THT-695), porting the shipped mobile flow (THT-685).
// Shared constants/types for the Request-a-Load flow, reused by the request
// modal and its tests so the payload shape and equipment values live in exactly
// one place (no string literals at the call sites).
//
// NOTE: the string values below are the app's canonical equipment/coverage set
// and MUST match the backend's accepted Joi set (see
// docs/spec-load-board-v1-backend.md / services/emails apiTypes.ts) and the
// mobile constant (tach-mobile/app/constants/loadRequest.ts). Coordinate any
// value change with the backend before touching this file.

export enum EquipmentType {
  DryVan = 'dry_van',
  Flatbed = 'flatbed',
  BoxTruck = 'box_truck',
  Hotshot = 'hotshot',
  StraightTruck = 'straight_truck',
  Reefer = 'reefer',
  PowerOnly = 'power_only',
  StepDeck = 'step_deck',
}

export interface EquipmentOption {
  value: EquipmentType;
  label: string;
}

// Dispatch pricing tiers (THT-691): Box truck and Hotshot price at
// `box_hotshot_pct`; everything else at `standard_pct`. The single source for
// the grouping — display logic must consume this set, never inline value lists.
export const PREMIUM_EQUIPMENT_TYPES: ReadonlySet<EquipmentType> = new Set([
  EquipmentType.BoxTruck,
  EquipmentType.Hotshot,
]);

// GET /api/v1/emails/load-request/pricing response (THT-691) — see
// docs/spec-dispatch-pricing-backend.md at the workspace root. Percentages are
// numbers (e.g. 7.5), resolved server-side from equipment tier + authority age.
export interface DispatchPricing {
  standard_pct: number;
  box_hotshot_pct: number;
  authority_over_1yr: boolean;
  authority_source: string;
}

// Rendered order of the multi-select. Labels mirror the mobile i18n copy.
export const EQUIPMENT_TYPE_OPTIONS: EquipmentOption[] = [
  { value: EquipmentType.DryVan, label: 'Dry Van' },
  { value: EquipmentType.Flatbed, label: 'Flatbed' },
  { value: EquipmentType.BoxTruck, label: 'Box Truck' },
  { value: EquipmentType.Hotshot, label: 'Hotshot' },
  { value: EquipmentType.StraightTruck, label: 'Straight Truck' },
  { value: EquipmentType.Reefer, label: 'Reefer' },
  { value: EquipmentType.PowerOnly, label: 'Power Only' },
  { value: EquipmentType.StepDeck, label: 'Step Deck' },
];

// How soon the carrier needs coverage, so the dispatcher understands urgency
// before reaching out. Values are the backend contract's accepted set.
export enum CoverageTiming {
  SameDay = 'same_day',
  NextDay = 'next_day',
  Future = 'future',
}

export interface CoverageTimingOption {
  value: CoverageTiming;
  label: string;
}

// Rendered order of the required single-select.
export const COVERAGE_TIMING_OPTIONS: CoverageTimingOption[] = [
  { value: CoverageTiming.SameDay, label: 'Same Day' },
  { value: CoverageTiming.NextDay, label: 'Next Day' },
  { value: CoverageTiming.Future, label: 'Future' },
];

// Same Day / Next Day resolve to a concrete device-local date shown on the
// option (and sent as `coverage_date`). Future has no fixed offset — the user
// picks the date.
export const COVERAGE_TIMING_DAY_OFFSETS: Partial<
  Record<CoverageTiming, number>
> = {
  [CoverageTiming.SameDay]: 0,
  [CoverageTiming.NextDay]: 1,
};

// John's bound for the Future picker: dates must be at least 2 days out
// (min = today + 2, no max).
export const FUTURE_MIN_OFFSET_DAYS = 2;

export type PickupLocationType = 'current' | 'manual';

// Matches the backend contract's `pickup_location` object.
export interface PickupLocation {
  type: PickupLocationType;
  display: string;
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;
}

// The POST /api/v1/emails/load-request request body — EXACTLY these keys. The
// backend schema is `.unknown(false).strict(true)`, so any extra field (incl.
// MC#/contact) is rejected with a 400. `coverage_date` is the resolved
// device-local ISO `yyyy-mm-dd` for the chosen coverage timing.
//
// `callback_phone` (THT-753) is the number the carrier confirmed on the review
// step, in E.164 US ("+1" + 10 digits) — the line dispatch calls back on, and
// contact detail only: identity is still derived server-side from the auth
// token. Optional on the wire by contract, because the backend falls back to
// the customer record's `phone_number` when it is absent.
export interface LoadRequestPayload {
  pickup_location: PickupLocation;
  equipment_types: EquipmentType[];
  coverage_timing: CoverageTiming;
  coverage_date?: string;
  callback_phone?: string;
}

// A 5-digit US ZIP; anything else typed manually is treated as a city name.
export const ZIP_REGEX = /^\d{5}$/;
